import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { isNavItemActive, useCustomerNavItems } from './customerNav';

/**
 * The customer app's navigation on desktop (lg and up), in the page header:
 * the same places as the phone's bottom bar, plus the cart with its count and
 * total — which on phones is the floating cart bar. Hidden below lg, where the
 * bottom bar and floating cart are used instead. Colours come from the store's
 * theme variables, so it matches every theme; a header whose background is the
 * primary colour itself (Blinkit's yellow) passes its own `activeClassName` and
 * `cartClassName` so they stay readable.
 */
const ACTIVE = 'text-[var(--theme-primary)] bg-[var(--theme-primary)]/10';
const CART = 'bg-[var(--theme-primary)] text-white';

export const CustomerDesktopNav = ({ showCart = true, className = '', activeClassName = ACTIVE, cartClassName = CART }) => {
  const items = useCustomerNavItems();
  const { pathname } = useLocation();
  const { totalItems, totalAmount } = useCart();
  const { storeSettings } = useAuth();
  const currency = storeSettings?.currency_symbol || '₹';

  return (
    <nav className={`hidden lg:flex items-center gap-1 ${className}`} aria-label="Main" data-testid="desktop-nav">
      {items.map((item) => {
        const active = isNavItemActive(item.path, pathname);
        return (
          <Link
            key={item.path}
            to={item.path}
            aria-current={active ? 'page' : undefined}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
              active ? activeClassName : 'text-[var(--theme-text-main)] hover:bg-black/5'
            }`}
            data-testid={`desktop-nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-4 h-4" aria-hidden="true" />
            {item.label}
          </Link>
        );
      })}
      {showCart && (
        <Link
          to="/cart"
          className={`ml-2 flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold shadow-sm hover:opacity-95 transition-opacity ${cartClassName}`}
          data-testid="desktop-cart-btn"
        >
          <ShoppingCart className="w-4 h-4" aria-hidden="true" />
          {totalItems > 0 ? (
            <span>
              {totalItems} item{totalItems > 1 ? 's' : ''} · {currency}{totalAmount.toFixed(0)}
            </span>
          ) : (
            <span>My Cart</span>
          )}
        </Link>
      )}
    </nav>
  );
};
